'use client';

import { useState } from 'react';
import { VisualizationWrapper } from './visualization-wrapper';

interface CycleDiagramProps {
  element: {
    props: {
      title: string;
      steps: Array<{
        id: string;
        label: string;
        description?: string;
        duration?: string;
      }>;
      direction: 'clockwise' | 'counterclockwise';
    };
    raw?: boolean;
    showLegend?: boolean;
    visualizationId?: string;
  };
}

// Modern, high-contrast accessible colors
const COLORS = [
  { main: '#3B82F6', dark: '#1D4ED8', text: '#EFF6FF' }, // Blue
  { main: '#10B981', dark: '#047857', text: '#ECFDF5' }, // Emerald
  { main: '#8B5CF6', dark: '#5B21B6', text: '#F5F3FF' }, // Violet
  { main: '#F59E0B', dark: '#B45309', text: '#FFFBEB' }, // Amber
  { main: '#EF4444', dark: '#B91C1C', text: '#FEF2F2' }, // Red
  { main: '#06B6D4', dark: '#0E7490', text: '#ECFEFF' }, // Cyan
  { main: '#EC4899', dark: '#BE185D', text: '#FDF2F8' }, // Pink
  { main: '#6366F1', dark: '#4338CA', text: '#EEF2FF' }, // Indigo
];

interface HoveredStep {
  step: {
    id: string;
    label: string;
    description?: string;
    duration?: string;
  };
  index: number;
  position: { x: number; y: number };
  color: typeof COLORS[0];
}

export function CycleDiagram({ element }: CycleDiagramProps) {
  const [hoveredStep, setHoveredStep] = useState<HoveredStep | null>(null);

  if (!element?.props) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-muted-foreground/50 border-2 border-dashed rounded-xl">
        Waiting for data...
      </div>
    );
  }
  
  const { title, steps, direction = 'clockwise' } = element.props;
  const safeSteps = steps || [];
  
  if (safeSteps.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-muted-foreground/50 border-2 border-dashed rounded-xl">
        No steps to display
      </div>
    );
  }

  // Geometry configuration — viewBox is larger than ring to fit labels
  const size = 900;
  const center = size / 2;
  const outerRadius = 160;
  const innerRadius = 90;
  const gapAngleDegrees = 4;
  
  const totalSteps = safeSteps.length;
  const effectiveSteps = Math.max(totalSteps, 1);
  const segmentAngle = 360 / effectiveSteps;

  const getCoordinatesForAngle = (angleInDegrees: number, radius: number) => {
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180);
    return {
      x: center + (radius * Math.cos(angleInRadians)),
      y: center + (radius * Math.sin(angleInRadians))
    };
  };

  const createSegmentPath = (index: number) => {
    const startAngle = index * segmentAngle + (gapAngleDegrees / 2);
    const endAngle = (index + 1) * segmentAngle - (gapAngleDegrees / 2);
    
    const p1 = getCoordinatesForAngle(startAngle, outerRadius);
    const p2 = getCoordinatesForAngle(endAngle, outerRadius);
    const p3 = getCoordinatesForAngle(endAngle + 6, (outerRadius + innerRadius) / 2);
    const p4 = getCoordinatesForAngle(endAngle, innerRadius);
    const p5 = getCoordinatesForAngle(startAngle, innerRadius);

    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
    const sweep = 1;

    return [
      `M ${p1.x} ${p1.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} ${sweep} ${p2.x} ${p2.y}`,
      `L ${p3.x} ${p3.y}`,
      `L ${p4.x} ${p4.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${p5.x} ${p5.y}`,
      'Z'
    ].join(' ');
  };

  const getLabelPosition = (index: number) => {
    const midAngle = (index * segmentAngle) + (segmentAngle / 2);
    const labelRadius = outerRadius + 60; 
    return getCoordinatesForAngle(midAngle, labelRadius);
  };
  
  const getNumberPosition = (index: number) => {
    const midAngle = (index * segmentAngle) + (segmentAngle / 2);
    const numberRadius = (outerRadius + innerRadius) / 2;
    return getCoordinatesForAngle(midAngle, numberRadius);
  };

  // Get popup position (outside the ring, offset based on angle)
  const getPopupPosition = (index: number) => {
    const midAngle = (index * segmentAngle) + (segmentAngle / 2);
    const popupRadius = outerRadius + 100;
    return getCoordinatesForAngle(midAngle, popupRadius);
  };

  const handleMouseEnter = (step: typeof safeSteps[0], index: number) => {
    const pos = getPopupPosition(index);
    setHoveredStep({
      step,
      index,
      position: pos,
      color: COLORS[index % COLORS.length]
    });
  };

  const handleMouseLeave = () => {
    setHoveredStep(null);
  };

  const content = (
    <div className="w-full h-full flex flex-col items-center justify-center bg-white dark:bg-zinc-950 p-6 relative">
      <div className="relative" style={{ width: '100%', height: '100%', maxHeight: '900px', maxWidth: '900px' }}>
        <svg 
          viewBox={`0 0 ${size} ${size}`} 
          className="w-full h-full drop-shadow-xl"
          style={{ overflow: 'visible' }}
        >
          <defs>
            {safeSteps.map((_, i) => {
               const color = COLORS[i % COLORS.length];
               return (
                <linearGradient key={`grad-${i}`} id={`segment-grad-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={color.main} />
                  <stop offset="100%" stopColor={color.dark} />
                </linearGradient>
               );
            })}
             <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
               <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.2" />
             </filter>
          </defs>

          {/* Center decorative element */}
          <circle cx={center} cy={center} r={innerRadius - 20} className="fill-slate-50 dark:fill-zinc-900 stroke-slate-200 dark:stroke-zinc-800" strokeWidth="1" />
          <text
            x={center}
            y={center}
            dominantBaseline="middle"
            textAnchor="middle"
            className="fill-slate-400 dark:fill-zinc-600 font-semibold tracking-wider uppercase"
            fontSize={Math.min(14, Math.max(9, 120 / Math.max((title || 'Process').length, 1) * 7))}
          >
            {title || 'Process'}
          </text>

          {/* Render Segments */}
          {safeSteps.map((step, index) => {
            const pathData = createSegmentPath(index);
            const numPos = getNumberPosition(index);
            const labelPos = getLabelPosition(index);
            const midAngle = (index * segmentAngle) + (segmentAngle / 2);
            
            const isRightSide = midAngle >= 0 && midAngle < 180;
            const textAnchor = isRightSide ? "start" : "end";
            const labelX = labelPos.x + (isRightSide ? 10 : -10);
            const isHovered = hoveredStep?.index === index;
            
            return (
              <g 
                key={step.id} 
                className="transition-all duration-200 cursor-pointer"
                style={{ transform: isHovered ? 'scale(1.03)' : 'scale(1)', transformOrigin: `${center}px ${center}px` }}
                onMouseEnter={() => handleMouseEnter(step, index)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Segment Path */}
                <path 
                  d={pathData} 
                  fill={`url(#segment-grad-${index % COLORS.length})`}
                  stroke="white" 
                  strokeWidth={isHovered ? "3" : "2"}
                  className="dark:stroke-zinc-950"
                  filter="url(#soft-shadow)"
                />
                
                {/* Step Number Circle */}
                <circle cx={numPos.x} cy={numPos.y} r="14" fill="white" fillOpacity="0.9" />
                <text 
                  x={numPos.x} 
                  y={numPos.y} 
                  dy="1"
                  dominantBaseline="middle" 
                  textAnchor="middle" 
                  className="fill-slate-900 text-[13px] font-bold font-sans"
                >
                  {index + 1}
                </text>

                {/* Connecting Line to Label */}
                <path 
                  d={`M ${getCoordinatesForAngle(midAngle, outerRadius).x} ${getCoordinatesForAngle(midAngle, outerRadius).y} L ${labelPos.x} ${labelPos.y} L ${labelX} ${labelPos.y}`}
                  fill="none"
                  stroke={COLORS[index % COLORS.length].main}
                  strokeWidth="2"
                  opacity={isHovered ? "1" : "0.5"}
                  className="transition-opacity"
                />

                {/* External Label */}
                <g transform={`translate(${labelX}, ${labelPos.y})`}>
                  <text
                    x="0"
                    y="-5"
                    textAnchor={textAnchor}
                    className="fill-slate-800 dark:fill-slate-100 text-[15px] font-bold font-sans"
                  >
                    {step.label}
                  </text>

                  {step.description && (() => {
                    // Word-wrap description into lines of ~45 chars
                    const maxChars = 45;
                    const words = step.description.split(' ');
                    const lines: string[] = [];
                    let current = '';
                    for (const word of words) {
                      if (current && (current + ' ' + word).length > maxChars) {
                        lines.push(current);
                        current = word;
                      } else {
                        current = current ? current + ' ' + word : word;
                      }
                    }
                    if (current) lines.push(current);
                    // Max 3 lines to keep labels compact
                    const displayLines = lines.slice(0, 3);
                    if (lines.length > 3) displayLines[2] = displayLines[2] + '...';
                    return (
                      <text
                        x="0"
                        y="14"
                        textAnchor={textAnchor}
                        className="fill-slate-500 dark:fill-slate-400 text-[11px] font-medium"
                      >
                        {displayLines.map((line, li) => (
                          <tspan key={li} x="0" dy={li === 0 ? 0 : 13}>{line}</tspan>
                        ))}
                      </text>
                    );
                  })()}

                  {step.duration && (
                    <text
                      x="0"
                      y={14 + (step.description ? Math.min(3, Math.ceil(step.description.length / 45)) * 13 + 4 : 14)}
                      textAnchor={textAnchor}
                      className="fill-slate-400 dark:fill-slate-500 text-[10px] uppercase tracking-wide font-semibold"
                    >
                      ⏱ {step.duration}
                    </text>
                  )}
                </g>
              </g>
            );
          })}
        </svg>

        {/* Hover Popup Card */}
        {hoveredStep && (
          <div 
            className="absolute z-50 pointer-events-none animate-in fade-in duration-200"
            style={{
              left: `${(hoveredStep.position.x / size) * 100}%`,
              top: `${(hoveredStep.position.y / size) * 100}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div 
              className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl p-4 border-2 min-w-[200px] max-w-[280px]"
              style={{ borderColor: hoveredStep.color.main }}
            >
              {/* Header with step number */}
              <div className="flex items-center gap-3 mb-2">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ background: `linear-gradient(135deg, ${hoveredStep.color.main}, ${hoveredStep.color.dark})` }}
                >
                  {hoveredStep.index + 1}
                </div>
                <h4 className="font-bold text-slate-800 dark:text-slate-100 text-base">
                  {hoveredStep.step.label}
                </h4>
              </div>
              
              {/* Description */}
              {hoveredStep.step.description && (
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 leading-relaxed">
                  {hoveredStep.step.description}
                </p>
              )}
              
              {/* Duration */}
              {hoveredStep.step.duration && (
                <div 
                  className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md w-fit"
                  style={{ backgroundColor: `${hoveredStep.color.main}20`, color: hoveredStep.color.dark }}
                >
                  <span>⏱</span>
                  <span>{hoveredStep.step.duration}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Screen reader legend */}
      <div className="hidden sr-only">
         {safeSteps.map((s, i) => <div key={i}>{s.label}</div>)}
      </div>
    </div>
  );

  if (element.raw) {
    return content;
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={650}
      exportData={{
        type: 'cycle-diagram',
        content: element.props
      }}
      visualizationId={element.visualizationId}
    >
      {content}
    </VisualizationWrapper>
  );
}
